import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const TeacherTasks = () => {
  const routes = all_routes;
  const [activeTab, setActiveTab] = useState("homework");

  // Mock data for tasks
  const tasksData = {
    homework: {
      pending: [
        {
          id: "1",
          type: "homework",
          class: "10A Mathematics",
          title: "Algebra Problems Ch.5",
          description: "Review and grade 32 submissions",
          dueDate: "2024-08-06",
          submissions: 28,
          totalStudents: 32,
          status: "grading",
          priority: "high",
        },
        {
          id: "2",
          type: "homework",
          class: "11B Physics",
          title: "Lab Report - Motion",
          description: "Check experimental data and conclusions",
          dueDate: "2024-08-07",
          submissions: 25,
          totalStudents: 28,
          status: "pending",
          priority: "medium",
        },
      ],
      completed: [
        {
          id: "3",
          type: "homework",
          class: "9C Mathematics",
          title: "Geometry Exercises",
          description: "All submissions graded",
          completedDate: "2024-08-04",
          submissions: 30,
          totalStudents: 30,
          status: "completed",
          avgGrade: "B+",
        },
      ],
    },
    exams: {
      upcoming: [
        {
          id: "4",
          type: "exam",
          class: "10A Mathematics",
          title: "Mid-term Examination",
          description: "Chapters 1-5 comprehensive test",
          examDate: "2024-08-10",
          duration: "2 hours",
          status: "scheduled",
          studentsCount: 32,
          priority: "high",
        },
        {
          id: "5",
          type: "exam",
          class: "11B Physics",
          title: "Mechanics Quiz",
          description: "Newton's laws and applications",
          examDate: "2024-08-12",
          duration: "1 hour",
          status: "draft",
          studentsCount: 28,
          priority: "medium",
        },
      ],
      grading: [
        {
          id: "6",
          type: "exam",
          class: "12A Advanced Math",
          title: "Calculus Test",
          description: "Derivatives and integrals",
          examDate: "2024-08-03",
          submissions: 25,
          totalStudents: 25,
          status: "grading",
          priority: "high",
        },
      ],
      completed: [
        {
          id: "7",
          type: "exam",
          class: "9C Mathematics",
          title: "Basic Algebra Test",
          description: "Foundational concepts assessment",
          completedDate: "2024-07-30",
          submissions: 30,
          totalStudents: 30,
          status: "completed",
          avgGrade: "A-",
        },
      ],
    },
  };

  const getTaskTypeColor = (type: string) => {
    switch (type) {
      case "homework":
        return "warning";
      case "exam":
        return "info";
      default:
        return "secondary";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "grading":
        return "primary";
      case "scheduled":
        return "info";
      case "draft":
        return "secondary";
      default:
        return "warning";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return "ti-check";
      case "grading":
        return "ti-edit";
      case "scheduled":
        return "ti-calendar";
      case "draft":
        return "ti-file-text";
      default:
        return "ti-clock";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "danger";
      case "medium":
        return "warning";
      case "low":
        return "info";
      default:
        return "secondary";
    }
  };

  const renderTaskCard = (task: any) => (
    <div key={task.id} className="card mb-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <div
              className={`avatar avatar-sm bg-${getStatusColor(
                task.status
              )}-transparent me-3`}
            >
              <i
                className={`ti ${getStatusIcon(
                  task.status
                )} text-${getStatusColor(task.status)}`}
              ></i>
            </div>
            <div>
              <h6 className="mb-1">{task.title}</h6>
              <p className="text-muted mb-0">
                <i className="ti ti-users me-1"></i>
                {task.class}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2">
            {task.priority && (
              <span className={`badge bg-${getPriorityColor(task.priority)}`}>
                {task.priority}
              </span>
            )}
            <span className={`badge bg-${getTaskTypeColor(task.type)}`}>
              {task.type}
            </span>
          </div>
        </div>

        <p className="text-muted mb-3">{task.description}</p>

        {/* Progress for homework/exams with submissions */}
        {task.submissions !== undefined && (
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="small text-muted">Submissions</span>
              <span className="small">
                {task.submissions}/{task.totalStudents || task.studentsCount}
              </span>
            </div>
            <div className="progress" style={{ height: "6px" }}>
              <div
                className={`progress-bar bg-${getStatusColor(task.status)}`}
                style={{
                  width: `${
                    (task.submissions /
                      (task.totalStudents || task.studentsCount)) *
                    100
                  }%`,
                }}
              ></div>
            </div>
          </div>
        )}

        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <i className="ti ti-calendar me-2 text-muted"></i>
            <span className="text-muted">
              {task.completedDate
                ? `Completed: ${new Date(
                    task.completedDate
                  ).toLocaleDateString()}`
                : task.examDate
                ? `Exam: ${new Date(task.examDate).toLocaleDateString()}`
                : `Due: ${new Date(task.dueDate).toLocaleDateString()}`}
            </span>
            {task.duration && (
              <>
                <span className="text-muted mx-2">•</span>
                <span className="text-muted">{task.duration}</span>
              </>
            )}
          </div>
          <div className="d-flex gap-2">
            {task.avgGrade && (
              <span className="badge bg-success">{task.avgGrade}</span>
            )}
            {task.status === "grading" && (
              <Link to={routes.gradeEntry} className="btn btn-primary btn-sm">
                <i className="ti ti-edit me-1"></i>Grade
              </Link>
            )}
            {task.status === "draft" && (
              <Link to={routes.manageHomework} className="btn btn-info btn-sm">
                <i className="ti ti-edit me-1"></i>Edit
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Tasks Management</h3>
              <p className="text-muted mb-0">Homework & Exams management</p>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-warning mb-2">
                    <i className="ti ti-book text-white fs-2"></i>
                  </div>
                  <h4 className="text-warning">
                    {tasksData.homework.pending.length}
                  </h4>
                  <p className="mb-0">Pending Homework</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-info-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-info mb-2">
                    <i className="ti ti-clipboard-list text-white fs-2"></i>
                  </div>
                  <h4 className="text-info">
                    {tasksData.exams.upcoming.length}
                  </h4>
                  <p className="mb-0">Upcoming Exams</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-primary mb-2">
                    <i className="ti ti-edit text-white fs-2"></i>
                  </div>
                  <h4 className="text-primary">
                    {tasksData.exams.grading.length}
                  </h4>
                  <p className="mb-0">Pending Grading</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-success mb-2">
                    <i className="ti ti-check text-white fs-2"></i>
                  </div>
                  <h4 className="text-success">
                    {tasksData.homework.completed.length +
                      tasksData.exams.completed.length}
                  </h4>
                  <p className="mb-0">Completed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Quick Actions</h5>
                </div>
                <div className="card-body">
                  <div className="row g-2">
                    <div className="col-md-3 col-sm-6">
                      <Link
                        to={routes.manageHomework}
                        className="btn btn-warning w-100"
                      >
                        <i className="ti ti-book me-2"></i>Create Homework
                      </Link>
                    </div>
                    <div className="col-md-3 col-sm-6">
                      <Link
                        to={routes.gradeEntry}
                        className="btn btn-info w-100"
                      >
                        <i className="ti ti-clipboard-list me-2"></i>Schedule
                        Exam
                      </Link>
                    </div>
                    <div className="col-md-3 col-sm-6">
                      <Link
                        to={routes.gradeEntry}
                        className="btn btn-primary w-100"
                      >
                        <i className="ti ti-edit me-2"></i>Grade Papers
                      </Link>
                    </div>
                    <div className="col-md-3 col-sm-6">
                      <Link
                        to={routes.attendanceTracker}
                        className="btn btn-success w-100"
                      >
                        <i className="ti ti-eye me-2"></i>View Reports
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content with Tabs */}
          <div className="card">
            <div className="card-header">
              <ul className="nav nav-tabs border-bottom-0" role="tablist">
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "homework" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("homework")}
                  >
                    <i className="ti ti-book me-2"></i>Homework
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "exams" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("exams")}
                  >
                    <i className="ti ti-clipboard-list me-2"></i>Exams
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
              {activeTab === "homework" && (
                <div>
                  {/* Homework Subtabs */}
                  <div className="mb-4">
                    <ul className="nav nav-pills">
                      <li className="nav-item">
                        <span className="nav-link active">
                          Pending ({tasksData.homework.pending.length})
                        </span>
                      </li>
                      <li className="nav-item">
                        <span className="nav-link text-muted">
                          Completed ({tasksData.homework.completed.length})
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Pending Homework */}
                  <div className="homework-list">
                    {tasksData.homework.pending.length > 0 ? (
                      tasksData.homework.pending.map((task) =>
                        renderTaskCard(task)
                      )
                    ) : (
                      <div className="text-center py-4">
                        <div className="avatar avatar-lg bg-warning-transparent mb-3">
                          <i className="ti ti-book fs-2 text-warning"></i>
                        </div>
                        <h5>No pending homework</h5>
                        <p className="text-muted">
                          All homework has been graded.
                        </p>
                        <Link
                          to={routes.manageHomework}
                          className="btn btn-warning"
                        >
                          Create New Homework
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "exams" && (
                <div>
                  {/* Exams Subtabs */}
                  <div className="mb-4">
                    <ul className="nav nav-pills">
                      <li className="nav-item">
                        <span className="nav-link active">
                          Upcoming ({tasksData.exams.upcoming.length})
                        </span>
                      </li>
                      <li className="nav-item">
                        <span className="nav-link text-muted">
                          Grading ({tasksData.exams.grading.length})
                        </span>
                      </li>
                      <li className="nav-item">
                        <span className="nav-link text-muted">
                          Completed ({tasksData.exams.completed.length})
                        </span>
                      </li>
                    </ul>
                  </div>

                  {/* Upcoming Exams */}
                  <div className="exams-list">
                    {tasksData.exams.upcoming.length > 0 ? (
                      tasksData.exams.upcoming.map((task) =>
                        renderTaskCard(task)
                      )
                    ) : (
                      <div className="text-center py-4">
                        <div className="avatar avatar-lg bg-info-transparent mb-3">
                          <i className="ti ti-clipboard-list fs-2 text-info"></i>
                        </div>
                        <h5>No upcoming exams</h5>
                        <p className="text-muted">
                          Schedule new exams for your classes.
                        </p>
                        <Link to={routes.gradeEntry} className="btn btn-info">
                          Schedule Exam
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherTasks;
